import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import type { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import { cloudRunViewerTf } from "@p0security/integrations-gcloud-shared/cloud-run-iac";
import type { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import type { DevEnv } from "@p0security/shared/types/environment";

import { toLambdaFunctionName } from "./hosting.ts";

// The custom role and the project bindings need cloudresourcemanager. The
// connector needs run and secretmanager.
const GCP_REQUIRED_SERVICES = [
  "cloudresourcemanager.googleapis.com",
  "iam.googleapis.com",
  "run.googleapis.com",
  "secretmanager.googleapis.com",
];

// The audience Entra expects on a federated credential's token in the public
// cloud. P0 requests its Google ID token for it.
const AZURE_TOKEN_EXCHANGE_AUDIENCE = "api://AzureADTokenExchange";

/** One shell command over several lines, for a copyable command block. */
export const shellLines = (...lines: string[]) => lines.join(" \\\n  ");

/** A Terraform name does not accept a hyphen. */
export const tfId = (name: string) => name.replace(/-/g, "_");

/** P0's own service account, which the connector is granted roles/run.invoker
 * for and checks every caller's token against. A tenant's copy of it is
 * recorded on the gcloud installation. */
export const p0InvokerEmail = (installed: Record<string, unknown>): string =>
  (installed.gcloud as GcloudIntegration | undefined)?.root?.[SINGLETON_ITEM]
    ?.serviceAccountEmail ?? "";

export type GcpConnectorTfParams = {
  /** Names the product in resource names and titles */
  names: {
    /** Terraform resource names start `p0_<resource>_`, e.g. "github" */
    resource: string;
    /** Custom role ids start `p0<role>Connector`, e.g. "Github" */
    role: string;
    /** Display names, e.g. "GitHub" */
    label: string;
  };
  /** Pin the image by digest, because a tag can be re-pushed. */
  image: { imageName: string; digest: string };
  projectId: string;
  region: string;
  connectorName: string;
  serviceAccountId: string;
  invokerEmail: string;
  /** GCP secret id prefix of the grant secrets that the connector manages;
   * omit it for a connector that makes no grant secrets */
  tokenSecretPrefix?: string;
  /** The secret that the install adds and the connector reads */
  installSecret: {
    /** Resource name part, e.g. "private_key" */
    resource: string;
    /** Terraform-safe item id, for resources that repeat per item */
    itemTfId: string;
    secretId: string;
    /** Names the secret in comments, e.g. "private key" */
    label: string;
  };
};

/** Lets the connector create and manage only the grant secrets whose id
 * starts with `tokenSecretPrefix`. */
const tokenSecretTf = (
  r: string,
  role: string,
  label: string,
  tokenSecretPrefix: string
) => `# GCP checks secretmanager.secrets.create on the project, so this binding
# cannot carry the condition that the management binding below does.
resource "google_project_iam_custom_role" "p0_${r}_connector_secret_create" {
  project     = local.project_id
  role_id     = "p0${role}ConnectorSecretCreate"
  title       = "P0 ${label} connector secret creation"
  permissions = ["secretmanager.secrets.create"]

  depends_on = [google_project_service.enable_services]
}

resource "google_project_iam_member" "p0_${r}_connector_secret_create" {
  project = local.project_id
  role    = google_project_iam_custom_role.p0_${r}_connector_secret_create.name
  member  = "serviceAccount:\${google_service_account.p0_${r}_connector.email}"
}

resource "google_project_iam_custom_role" "p0_${r}_connector_secret_manage" {
  project = local.project_id
  role_id = "p0${role}ConnectorSecretManage"
  title   = "P0 ${label} connector access token management"
  permissions = [
    "secretmanager.secrets.delete",
    "secretmanager.secrets.get",
    "secretmanager.versions.add",
  ]

  depends_on = [google_project_service.enable_services]
}

resource "google_project_iam_member" "p0_${r}_connector_secret_manage" {
  project = local.project_id
  role    = google_project_iam_custom_role.p0_${r}_connector_secret_manage.name
  member  = "serviceAccount:\${google_service_account.p0_${r}_connector.email}"

  # A condition matches on the resource name, which carries the project number
  # and not the project id.
  condition {
    title      = "P0 ${label} access token secrets"
    expression = "resource.name.startsWith('projects/\${data.google_project.p0_${r}_connector.number}/secrets/${tokenSecretPrefix}')"
  }
}

`;

/** Terraform that deploys a connector on Cloud Run, lets P0 invoke it, and
 * lets it manage only its own grant secrets, if it has any.
 *
 * Only the install secret's resources carry an item suffix, so a second item
 * in the same project makes duplicate connector blocks.
 */
export const gcpConnectorTf = ({
  names: { resource: r, role, label },
  image,
  projectId,
  region,
  connectorName,
  serviceAccountId,
  invokerEmail,
  tokenSecretPrefix,
  installSecret,
}: GcpConnectorTfParams) => `locals {
  project_id     = "${projectId}"
  region         = "${region}"
  connector_name = "${connectorName}"
  invoker_email  = "${invokerEmail}"
}

resource "google_project_service" "enable_services" {
  project = local.project_id
  for_each = toset([
    ${GCP_REQUIRED_SERVICES.map((service) => `"${service}"`).join(",\n    ")}
  ])
  service            = each.key
  disable_on_destroy = false
}

resource "google_service_account" "p0_${r}_connector" {
  project      = local.project_id
  account_id   = "${serviceAccountId}"
  display_name = "P0 Cloud Run ${label} connector"

  depends_on = [google_project_service.enable_services]
}

resource "google_cloud_run_v2_service" "p0_${r}_connector" {
  project             = local.project_id
  name                = local.connector_name
  location            = local.region
  deletion_protection = false
  # P0 calls the connector from outside GCP; access is gated by IAM, not origin.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.p0_${r}_connector.email

    containers {
      image = "${image.imageName}:${image.digest}"

      # The connector checks this against the caller's OIDC token, so it is
      # not redundant with the roles/run.invoker grant.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = local.invoker_email
      }
    }
  }

  depends_on = [google_project_service.enable_services]
}

resource "google_cloud_run_v2_service_iam_member" "p0_${r}_invoker" {
  project  = local.project_id
  location = local.region
  name     = google_cloud_run_v2_service.p0_${r}_connector.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:\${local.invoker_email}"
}

${cloudRunViewerTf({
  resource: `p0_${r}_viewer`,
  project: "local.project_id",
  location: "local.region",
  service: `google_cloud_run_v2_service.p0_${r}_connector.name`,
  // eslint-disable-next-line no-template-curly-in-string
  member: "${local.invoker_email}",
})}

data "google_project" "p0_${r}_connector" {
  project_id = local.project_id
}

${tokenSecretPrefix === undefined ? "" : tokenSecretTf(r, role, label, tokenSecretPrefix)}resource "google_secret_manager_secret" "p0_${r}_${installSecret.resource}_${installSecret.itemTfId}" {
  project   = local.project_id
  secret_id = "${installSecret.secretId}"

  replication {
    auto {}
  }

  depends_on = [google_project_service.enable_services]
}

resource "google_secret_manager_secret_iam_member" "p0_${r}_${installSecret.resource}_${installSecret.itemTfId}" {
  project   = local.project_id
  secret_id = google_secret_manager_secret.p0_${r}_${installSecret.resource}_${installSecret.itemTfId}.secret_id
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${google_service_account.p0_${r}_connector.email}"
}

# The accessor role gives secretmanager.versions.access only. The connector also
# reads the secret itself, and the management role below covers the access token
# secrets alone, so the ${installSecret.label} needs its own grant.
resource "google_secret_manager_secret_iam_member" "p0_${r}_${installSecret.resource}_read_${installSecret.itemTfId}" {
  project   = local.project_id
  secret_id = google_secret_manager_secret.p0_${r}_${installSecret.resource}_${installSecret.itemTfId}.secret_id
  role      = "roles/secretmanager.viewer"
  member    = "serviceAccount:\${google_service_account.p0_${r}_connector.email}"
}
`;

/** The role that P0 invokes an AWS connector as. */
export const p0InvokerRoleName = (context: DevEnv): string =>
  awsRoleName(context, "iam-write");

export type AwsConnectorTfParams = {
  /** Names the product in resource names and titles */
  names: {
    /** Terraform resource names start `p0_<resource>_`, e.g. "github" */
    resource: string;
    /** Display names, e.g. "GitHub" */
    label: string;
  };
  /** The image on Docker Hub, e.g. "p0security/p0-connector-acme-aws", and
   * its `<tag>@sha256:<digest>`. Pin by digest, because a tag can be
   * re-pushed. */
  image: { imageName: string; digest: string };
  /** The vault's account, which also runs the connector */
  accountId: string;
  /** The connector's region */
  region: string;
  secretsRegion: string;
  /** The connector's base name; the function is named by
   * {@link toLambdaFunctionName} */
  connectorName: string;
  /** From {@link p0InvokerRoleName} */
  p0RoleName: string;
  /** Name prefix of the grant secrets that the connector manages; omit it for
   * a connector that makes no grant secrets */
  tokenSecretPrefix?: string;
  /** The secret that the install adds and the connector reads */
  installSecret: {
    /** Resource name part, e.g. "private_key" */
    resource: string;
    /** Terraform-safe item id, for resources that repeat per item */
    itemTfId: string;
    /** The secret's name */
    secretId: string;
    /** Names the secret in comments, e.g. "private key" */
    label: string;
  };
};

/** Lets the connector create and manage only the grant secrets whose name
 * starts with `tokenSecretPrefix`. */
const awsTokenSecretTf = (r: string, tokenSecretPrefix: string) => `

# The connector makes, changes and removes its grant secrets. It cannot read
# them back.
resource "aws_iam_role_policy" "p0_${r}_connector_grant_secrets" {
  provider = aws.p0_${r}

  name = "ManageGrantSecrets"
  role = aws_iam_role.p0_${r}_connector.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = [
        "secretsmanager:CreateSecret",
        "secretsmanager:DeleteSecret",
        "secretsmanager:DescribeSecret",
        "secretsmanager:PutSecretValue",
      ]
      Resource = "arn:\${data.aws_partition.p0_${r}.partition}:secretsmanager:\${local.p0_${r}.secrets_region}:\${local.p0_${r}.account_id}:secret:${tokenSecretPrefix}*"
    }]
  })
}`;

/** Terraform that deploys a connector on AWS Lambda, lets P0 invoke it, and
 * lets it manage only its own grant secrets, if it has any.
 *
 * Whoever applies it needs Docker and a signed-in AWS CLI, to copy the image
 * into ECR. Only the install secret's resources carry an item suffix, so a
 * second item in the same account makes duplicate connector blocks.
 */
export const awsConnectorTf = ({
  names: { resource: r, label },
  image,
  accountId,
  region,
  secretsRegion,
  connectorName,
  p0RoleName,
  tokenSecretPrefix,
  installSecret,
}: AwsConnectorTfParams) => {
  const [imageTag, imageDigest] = image.digest.split("@");
  const secret = `p0_${r}_${installSecret.resource}_${installSecret.itemTfId}`;
  return `terraform {
  # terraform_data needs Terraform 1.4.
  required_version = ">= 1.4"

  # If your configuration already has a required_providers block, add docker to
  # it and remove this one.
  required_providers {
    docker = {
      source  = "kreuzwerker/docker"
      version = "~> 3.0"
    }
  }
}

locals {
  p0_${r} = {
    account_id     = "${accountId}"
    region         = "${region}"
    secrets_region = "${secretsRegion}"
    function_name  = "${toLambdaFunctionName(connectorName)}"
    p0_role_name   = "${p0RoleName}"
    image_tag      = "${imageTag}"
    image_digest   = "${imageDigest}"
  }
}

# P0 finds the connector by this account, region and function name, so deploy
# it exactly there. Add the credential settings that your other AWS providers
# use, if they have any.
provider "aws" {
  alias               = "p0_${r}"
  region              = local.p0_${r}.region
  allowed_account_ids = [local.p0_${r}.account_id]
}

provider "aws" {
  alias               = "p0_${r}_secrets"
  region              = local.p0_${r}.secrets_region
  allowed_account_ids = [local.p0_${r}.account_id]
}

data "aws_partition" "p0_${r}" {
  provider = aws.p0_${r}
}

data "docker_registry_image" "p0_${r}_connector" {
  name = "${image.imageName}:\${local.p0_${r}.image_tag}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == local.p0_${r}.image_digest
      error_message = "The connector image's tag does not resolve to the digest pinned here. Copy the current Terraform from P0's ${label} installer."
    }
  }
}

resource "aws_ecr_repository" "p0_${r}_connector" {
  provider = aws.p0_${r}

  name = local.p0_${r}.function_name
  # Nobody who can push to this repository can replace the image that runs.
  image_tag_mutability = "IMMUTABLE"
  force_delete         = true

  image_scanning_configuration {
    scan_on_push = true
  }
}

# ECR tags are immutable, so each copy pushes a tag of its own.
resource "terraform_data" "p0_${r}_connector_image" {
  triggers_replace = {
    digest         = data.docker_registry_image.p0_${r}_connector.sha256_digest
    repository_url = aws_ecr_repository.p0_${r}_connector.repository_url
    tag            = local.p0_${r}.image_tag
  }

  provisioner "local-exec" {
    command = <<-EOT
      set -e
      trap 'docker logout "$REGISTRY" || true' EXIT
      aws ecr get-login-password --region "$REGION" |
        docker login --username AWS --password-stdin "$REGISTRY"
      docker pull --platform linux/amd64 "$SOURCE"
      docker tag "$SOURCE" "$TARGET"
      docker push "$TARGET"
    EOT

    environment = {
      REGION   = local.p0_${r}.region
      REGISTRY = split("/", aws_ecr_repository.p0_${r}_connector.repository_url)[0]
      SOURCE   = "${image.imageName}@\${local.p0_${r}.image_digest}"
      TARGET   = "\${aws_ecr_repository.p0_${r}_connector.repository_url}:\${local.p0_${r}.image_tag}-\${self.id}"
    }
  }
}

# The push gives ECR the manifest of one platform, so its digest can differ
# from the digest pinned above. Lambda needs the ECR digest.
data "aws_ecr_image" "p0_${r}_connector" {
  provider = aws.p0_${r}

  repository_name = aws_ecr_repository.p0_${r}_connector.name
  image_tag       = "\${local.p0_${r}.image_tag}-\${terraform_data.p0_${r}_connector_image.id}"
}

resource "aws_iam_role" "p0_${r}_connector" {
  provider = aws.p0_${r}

  name = local.p0_${r}.function_name
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { Service = "lambda.amazonaws.com" }
      Action    = "sts:AssumeRole"
    }]
  })
}

resource "aws_cloudwatch_log_group" "p0_${r}_connector" {
  provider = aws.p0_${r}

  name              = "/aws/lambda/\${local.p0_${r}.function_name}"
  retention_in_days = 30
}

resource "aws_iam_role_policy" "p0_${r}_connector_logs" {
  provider = aws.p0_${r}

  name = "WriteConnectorLogs"
  role = aws_iam_role.p0_${r}_connector.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["logs:CreateLogStream", "logs:PutLogEvents"]
      Resource = "\${aws_cloudwatch_log_group.p0_${r}_connector.arn}:*"
    }]
  })
}

# No vpc_config: the connector calls ${label} over the internet.
resource "aws_lambda_function" "p0_${r}_connector" {
  provider = aws.p0_${r}

  function_name = local.p0_${r}.function_name
  description   = "P0 ${label} connector"
  role          = aws_iam_role.p0_${r}_connector.arn
  package_type  = "Image"
  image_uri     = "\${aws_ecr_repository.p0_${r}_connector.repository_url}@\${data.aws_ecr_image.p0_${r}_connector.image_digest}"
  architectures = ["x86_64"]
  memory_size   = 512
  timeout       = 60

  # The connector's role cannot make a log group.
  depends_on = [aws_cloudwatch_log_group.p0_${r}_connector]
}

# P0 invokes the connector as its role in this account. Keep this policy apart
# from P0's own policy on the role, which P0 checks for drift.
resource "aws_iam_role_policy" "p0_${r}_invoke" {
  provider = aws.p0_${r}

  name = "\${local.p0_${r}.function_name}-invoke"
  role = local.p0_${r}.p0_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "lambda:InvokeFunction"
      Resource = aws_lambda_function.p0_${r}_connector.arn
    }]
  })
}${tokenSecretPrefix === undefined ? "" : awsTokenSecretTf(r, tokenSecretPrefix)}

resource "aws_secretsmanager_secret" "${secret}" {
  provider = aws.p0_${r}_secrets

  name = "${installSecret.secretId}"
  # A name in its recovery window cannot be used again, so a reinstall fails.
  recovery_window_in_days = 0
}

# The connector reads the ${installSecret.label}, and no other install secret.
resource "aws_iam_role_policy" "${secret}" {
  provider = aws.p0_${r}

  name = "Read_${installSecret.resource}_${installSecret.itemTfId}"
  role = aws_iam_role.p0_${r}_connector.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "secretsmanager:GetSecretValue"
      Resource = aws_secretsmanager_secret.${secret}.arn
    }]
  })
}
`;
};

/** The Container App that a connector's app registration fronts. */
export type AzureContainerAppRef =
  /** The customer's own app, which the Terraform looks up in the azurerm
   * provider's subscription */
  | { type: "existing"; name: string; resourceGroupName: string }
  /** Declared in the same Terraform, e.g. "azurerm_container_app.p0_acme_connector" */
  | { type: "resource"; address: string };

export type AzureConnectorTrustTfParams = {
  /** Names the product in resource names and titles */
  names: {
    /** Terraform resource names start `p0_<resource>_`, e.g. "acme" */
    resource: string;
    /** Display names, e.g. "Acme" */
    label: string;
  };
  containerApp: AzureContainerAppRef;
  /** The Invoker identity that the connector trusts. Entra matches both
   * exactly against the `iss` and `sub` of the token P0 presents. */
  invoker: {
    /** e.g. "https://accounts.google.com" */
    issuer: string;
    /** For a GCP service account, its numeric unique ID: the `azure` root
     * install's `serviceAccountId` */
    subject: string;
  };
  /** The `azure-app` install's `clientServicePrincipalId`, which P0 reads the
   * Container App as */
  readerPrincipalId: string;
};

const containerAppAddress = (r: string, containerApp: AzureContainerAppRef) =>
  containerApp.type === "resource"
    ? containerApp.address
    : `data.azurerm_container_app.p0_${r}_connector`;

/** Terraform that lets P0 call an Azure Container App connector and read the
 * app back.
 *
 * It adds a single-tenant app registration whose federated credential trusts
 * the Invoker identity, and Reader on the Container App for P0's service
 * principal. The app registration is `azuread_application.p0_<resource>_connector`,
 * so a caller can wire Easy Auth and the connector's env to its client ID. The
 * client ID is also output as `p0_<resource>_connector_client_id`.
 *
 * The caller declares the `azuread` and `azurerm` providers.
 */
export const azureConnectorTrustTf = ({
  names: { resource: r, label },
  containerApp,
  invoker,
  readerPrincipalId,
}: AzureConnectorTrustTfParams) => {
  const app = containerAppAddress(r, containerApp);
  const lookup =
    containerApp.type === "existing"
      ? `data "azurerm_container_app" "p0_${r}_connector" {
  name                = "${containerApp.name}"
  resource_group_name = "${containerApp.resourceGroupName}"
}

`
      : "";
  return `${lookup}data "azuread_client_config" "p0_${r}_connector" {}

# The person applying owns the app registration, so the Application Developer
# role, which can manage only the apps its holder owns, can keep managing it.
resource "azuread_application" "p0_${r}_connector" {
  display_name     = "P0 ${label} connector"
  sign_in_audience = "AzureADMyOrg"
  owners           = [data.azuread_client_config.p0_${r}_connector.object_id]

  # Easy Auth and the connector expect the v2 issuer, which is tenant-pinned.
  api {
    requested_access_token_version = 2
  }

  # azuread_application_identifier_uri below manages the identifier URI.
  lifecycle {
    ignore_changes = [identifier_uris]
  }
}

# P0 requests a token for api://<client ID>/.default, and Entra resolves that
# scope through the identifier URI. An application cannot reference its own
# client ID, so the URI is a separate resource.
resource "azuread_application_identifier_uri" "p0_${r}_connector" {
  application_id = azuread_application.p0_${r}_connector.id
  identifier_uri = "api://\${azuread_application.p0_${r}_connector.client_id}"
}

resource "azuread_service_principal" "p0_${r}_connector" {
  client_id = azuread_application.p0_${r}_connector.client_id
  owners    = [data.azuread_client_config.p0_${r}_connector.object_id]
}

# P0 signs in as this app registration with its own identity's token instead
# of a secret.
resource "azuread_application_federated_identity_credential" "p0_${r}_invoker" {
  application_id = azuread_application.p0_${r}_connector.id
  display_name   = "p0-invoker"
  issuer         = "${invoker.issuer}"
  subject        = "${invoker.subject}"
  audiences      = ["${AZURE_TOKEN_EXCHANGE_AUDIENCE}"]
}

# P0 reads the Container App to get its URL and client ID when it installs.
resource "azurerm_role_assignment" "p0_${r}_reader" {
  scope                = ${app}.id
  role_definition_name = "Reader"
  principal_id         = "${readerPrincipalId}"
  principal_type       = "ServicePrincipal"
}

output "p0_${r}_connector_client_id" {
  value = azuread_application.p0_${r}_connector.client_id
}
`;
};

// The issuer of the Google ID tokens that P0's service account presents to
// Entra. The `azure` root install records the account's numeric ID, which is
// the tokens' subject.
const GOOGLE_ISSUER = "https://accounts.google.com";

/** What {@link azureConnectorTf} reads from P0's Azure installation: the
 * directory and service account of the `azure` root install, and the service
 * principal of its `azure-app` install. Undefined unless both are installed,
 * which {@link azureConnectorPrerequisiteMessage} tells the admin. */
export const azureConnectorTrustFor = (
  installed: Record<string, unknown>
):
  | Pick<
      AzureConnectorTfParams,
      "directoryId" | "invoker" | "readerPrincipalId"
    >
  | undefined => {
  const azure = installed.azure as Partial<AzureIntegration> | undefined;
  const root = azure?.root?.[SINGLETON_ITEM];
  const app = azure?.["azure-app"]?.[SINGLETON_ITEM];
  if (
    !root?.directoryId ||
    !root.serviceAccountId ||
    !app?.clientServicePrincipalId
  )
    return undefined;
  return {
    directoryId: root.directoryId,
    invoker: { issuer: GOOGLE_ISSUER, subject: root.serviceAccountId },
    readerPrincipalId: app.clientServicePrincipalId,
  };
};

/** Why a connector can't be deployed on Azure yet, if it can't: the Azure
 * integration, with its app registration, must be installed first. */
export const azureConnectorPrerequisiteMessage = (
  installed: Record<string, unknown>
) =>
  azureConnectorTrustFor(installed)
    ? undefined
    : "Install Microsoft Azure in P0, including its app registration, before deploying the connector on Azure.";

/** The roles that whoever applies {@link azureConnectorTf} needs, each with a
 * Microsoft Learn page about it. */
export const AZURE_CONNECTOR_APPLIER_ROLES = [
  {
    description:
      "Application Developer in Microsoft Entra ID, or a tenant that lets users register applications, to create the connector's app registration.",
    learnUrl:
      "https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/permissions-reference#application-developer",
  },
  {
    description:
      "Contributor and Role Based Access Control Administrator on the subscription, or Owner, to deploy the connector and grant it and P0 their roles.",
    learnUrl:
      "https://learn.microsoft.com/en-us/azure/role-based-access-control/built-in-roles/privileged",
  },
] as const;

/** The Container App env var that names an Azure connector's dedicated Key
 * Vault. The connector gets its vault's URL with each request instead; P0
 * reads this at install, since the vault's name is random. */
export const AZURE_KEY_VAULT_NAME_ENV = "P0_KEY_VAULT_NAME";

export type AzureConnectorTfParams = Pick<
  AzureConnectorTrustTfParams,
  "invoker" | "names" | "readerPrincipalId"
> & {
  /** Pin the image by digest, because a tag can be re-pushed. */
  image: { imageName: string; digest: string };
  /** The Entra directory (tenant) to deploy in: the `azure` root install's
   * `directoryId` */
  directoryId: string;
  subscriptionId: string;
  /** An Azure region, e.g. "eastus" */
  region: string;
  /** Names the Container App and its resource group */
  connectorName: string;
};

/** Terraform that deploys a connector on Azure Container Apps, with a
 * dedicated Key Vault for its secrets, and lets P0 call it and read it back.
 *
 * It declares its own providers. Its resource group is named
 * `connectorName`, as is the Container App, so that P0 can find the app at
 * install.
 */
export const azureConnectorTf = ({
  names,
  image,
  directoryId,
  subscriptionId,
  region,
  connectorName,
  invoker,
  readerPrincipalId,
}: AzureConnectorTfParams) => {
  const { resource: r } = names;
  return `terraform {
  required_providers {
    azapi = {
      source  = "Azure/azapi"
      version = "~> 2"
    }
    azuread = {
      source  = "hashicorp/azuread"
      version = "~> 3"
    }
    azurerm = {
      source  = "hashicorp/azurerm"
      version = "~> 4"
    }
    random = {
      source  = "hashicorp/random"
      version = "~> 3"
    }
  }
}

locals {
  tenant_id       = "${directoryId}"
  subscription_id = "${subscriptionId}"
  location        = "${region}"
  connector_name  = "${connectorName}"
}

provider "azurerm" {
  tenant_id       = local.tenant_id
  subscription_id = local.subscription_id
  features {}
}

provider "azuread" {
  tenant_id = local.tenant_id
}

provider "azapi" {
  tenant_id       = local.tenant_id
  subscription_id = local.subscription_id
}

resource "azurerm_resource_group" "p0_${r}_connector" {
  name     = local.connector_name
  location = local.location
}

resource "azurerm_log_analytics_workspace" "p0_${r}_connector" {
  name                = local.connector_name
  location            = azurerm_resource_group.p0_${r}_connector.location
  resource_group_name = azurerm_resource_group.p0_${r}_connector.name
  sku                 = "PerGB2018"
  retention_in_days   = 30
}

# A Consumption environment without a VNet. A VNet can only be added by
# recreating the environment.
resource "azurerm_container_app_environment" "p0_${r}_connector" {
  name                       = local.connector_name
  location                   = azurerm_resource_group.p0_${r}_connector.location
  resource_group_name        = azurerm_resource_group.p0_${r}_connector.name
  log_analytics_workspace_id = azurerm_log_analytics_workspace.p0_${r}_connector.id
}

# Key Vault names are global and stay reserved while a deleted vault is
# soft-deleted, so each install gets a new one.
resource "random_string" "p0_${r}_key_vault" {
  length  = 12
  special = false
  upper   = false
}

# Key Vault roles can't be limited to some secrets by name, so the connector
# gets a vault of its own. Purge protection is off so that a deleted vault can
# be purged and its name reused.
resource "azurerm_key_vault" "p0_${r}_connector" {
  name                       = "p0-\${random_string.p0_${r}_key_vault.result}"
  location                   = azurerm_resource_group.p0_${r}_connector.location
  resource_group_name        = azurerm_resource_group.p0_${r}_connector.name
  tenant_id                  = local.tenant_id
  sku_name                   = "standard"
  rbac_authorization_enabled = true
  purge_protection_enabled   = false
}

# The connector's own identity, for reading its secrets. P0 calls the
# connector through the app registration below instead.
resource "azurerm_user_assigned_identity" "p0_${r}_connector" {
  name                = local.connector_name
  location            = azurerm_resource_group.p0_${r}_connector.location
  resource_group_name = azurerm_resource_group.p0_${r}_connector.name
}

resource "azurerm_role_assignment" "p0_${r}_connector_secrets" {
  scope                = azurerm_key_vault.p0_${r}_connector.id
  role_definition_name = "Key Vault Secrets User"
  principal_id         = azurerm_user_assigned_identity.p0_${r}_connector.principal_id
  principal_type       = "ServicePrincipal"
}

# One replica that is always on, so that a cold start never counts against
# P0's timeout for a connector call.
resource "azurerm_container_app" "p0_${r}_connector" {
  name                         = local.connector_name
  container_app_environment_id = azurerm_container_app_environment.p0_${r}_connector.id
  resource_group_name          = azurerm_resource_group.p0_${r}_connector.name
  revision_mode                = "Single"

  identity {
    type         = "UserAssigned"
    identity_ids = [azurerm_user_assigned_identity.p0_${r}_connector.id]
  }

  ingress {
    external_enabled = true
    target_port      = 8080
    traffic_weight {
      latest_revision = true
      percentage      = 100
    }
  }

  template {
    min_replicas = 1
    max_replicas = 1

    container {
      name   = "connector"
      image  = "${image.imageName}:${image.digest}"
      cpu    = 0.25
      memory = "0.5Gi"

      # DefaultAzureCredential signs in as the identity this names.
      env {
        name  = "AZURE_CLIENT_ID"
        value = azurerm_user_assigned_identity.p0_${r}_connector.client_id
      }

      # The connector checks every caller's token against these, so it is not
      # redundant with Easy Auth.
      env {
        name  = "P0_INVOKER_TENANT_ID"
        value = local.tenant_id
      }

      env {
        name  = "P0_INVOKER_CLIENT_ID"
        value = azuread_application.p0_${r}_connector.client_id
      }

      env {
        name  = "${AZURE_KEY_VAULT_NAME_ENV}"
        value = azurerm_key_vault.p0_${r}_connector.name
      }
    }
  }
}

${azureConnectorTrustTf({
  names,
  containerApp: {
    type: "resource",
    address: `azurerm_container_app.p0_${r}_connector`,
  },
  invoker,
  readerPrincipalId,
})}
# azurerm has no Easy Auth for Container Apps. Easy Auth admits only tokens
# from this tenant for the connector's app registration, and answers anything
# else with a 401.
resource "azapi_resource" "p0_${r}_connector_auth" {
  type      = "Microsoft.App/containerApps/authConfigs@2024-03-01"
  name      = "current"
  parent_id = azurerm_container_app.p0_${r}_connector.id

  body = {
    properties = {
      platform = {
        enabled = true
      }
      globalValidation = {
        unauthenticatedClientAction = "Return401"
      }
      identityProviders = {
        azureActiveDirectory = {
          enabled = true
          registration = {
            clientId     = azuread_application.p0_${r}_connector.client_id
            openIdIssuer = "https://login.microsoftonline.com/\${local.tenant_id}/v2.0"
          }
          validation = {
            allowedAudiences = [azuread_application.p0_${r}_connector.client_id]
            defaultAuthorizationPolicy = {
              allowedApplications = [azuread_application.p0_${r}_connector.client_id]
            }
          }
        }
      }
    }
  }
}

output "p0_${r}_connector_fqdn" {
  value = azurerm_container_app.p0_${r}_connector.ingress[0].fqdn
}

output "p0_${r}_key_vault_name" {
  value = azurerm_key_vault.p0_${r}_connector.name
}
`;
};
